import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Target, Calendar, TrendingUp, Users, Loader2, Heart,
  Clock, MapPin, ArrowRight
} from 'lucide-react';
import { doadorApiCall } from './DoadorPortalLayout';
import { toast } from 'sonner';

export default function DoadorCampanhasPage() {
  const [loading, setLoading] = useState(true);
  const [campanhas, setCampanhas] = useState([]);

  useEffect(() => {
    loadCampanhas();
  }, []);

  const loadCampanhas = async () => {
    try {
      const data = await doadorApiCall('/doador/campanhas');
      setCampanhas(data.campanhas || []);
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const getTipoColor = (tipo) => {
    const colors = {
      'Geral': 'bg-blue-100 text-blue-700',
      'Gap Específico': 'bg-red-100 text-red-700',
      'Emergência': 'bg-red-500 text-white',
      'Sazonal': 'bg-amber-100 text-amber-700',
      'Angariação de Fundos': 'bg-green-100 text-green-700'
    };
    return colors[tipo] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doador-campanhas-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Target className="w-7 h-7 text-green-500" />
          Campanhas Ativas
        </h1>
        <p className="text-slate-500 mt-1">Participe nas nossas campanhas e faça a diferença</p>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">O seu contributo importa!</h2>
              <p className="text-green-100 mt-1">
                Cada doação ajuda-nos a apoiar mais famílias em necessidade.
              </p>
            </div>
            <Heart className="w-16 h-16 text-white/30" />
          </div>
        </CardContent>
      </Card>

      {/* Campanhas Grid */}
      {campanhas.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Target className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Sem campanhas ativas no momento</h3>
              <p className="text-slate-500 mt-1">Novas campanhas serão anunciadas em breve</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {campanhas.map((campanha) => {
            const progresso = campanha.meta ? (campanha.total_angariado / campanha.meta) * 100 : 0;
            const diasRestantes = campanha.data_fim 
              ? Math.max(0, Math.ceil((new Date(campanha.data_fim) - new Date()) / (1000 * 60 * 60 * 24)))
              : null;

            return (
              <Card key={campanha.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-2 bg-gradient-to-r from-rose-500 to-amber-500"></div>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className={getTipoColor(campanha.tipo)}>
                        {campanha.tipo}
                      </Badge>
                      <CardTitle className="mt-2">{campanha.nome}</CardTitle>
                    </div>
                    {campanha.urgente && (
                      <Badge className="bg-red-500 text-white animate-pulse">
                        Urgente
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-600 text-sm line-clamp-2">
                    {campanha.descricao || 'Ajude-nos a alcançar os nossos objetivos nesta campanha.'}
                  </p>

                  {/* Progress */}
                  {campanha.meta && (
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Progresso</span>
                        <span className="font-bold text-green-600">
                          {campanha.total_angariado?.toLocaleString('pt-PT')}€ / {campanha.meta?.toLocaleString('pt-PT')}€
                        </span>
                      </div>
                      <Progress value={Math.min(progresso, 100)} className="h-2" />
                      <p className="text-xs text-slate-500 mt-1">
                        {progresso.toFixed(0)}% da meta atingida
                      </p>
                    </div>
                  )}

                  {/* Info Row */}
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    {campanha.data_fim && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {diasRestantes !== null 
                            ? `${diasRestantes} dias restantes`
                            : new Date(campanha.data_fim).toLocaleDateString('pt-PT')
                          }
                        </span>
                      </div>
                    )}
                    {campanha.num_participantes && (
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{campanha.num_participantes} participantes</span>
                      </div>
                    )}
                  </div>

                  {/* CTA */}
                  <Button className="w-full bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600">
                    <Heart className="w-4 h-4 mr-2" />
                    Quero Participar
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* How to Help */}
      <Card>
        <CardHeader>
          <CardTitle>Como pode ajudar?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-rose-50 rounded-xl text-center">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="w-6 h-6 text-rose-500" />
              </div>
              <h3 className="font-semibold text-slate-800">Doar Produtos</h3>
              <p className="text-sm text-slate-500 mt-1">Roupa, alimentos, material escolar</p>
            </div>
            <div className="p-4 bg-green-50 rounded-xl text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <h3 className="font-semibold text-slate-800">Doação Financeira</h3>
              <p className="text-sm text-slate-500 mt-1">Contribua com o valor que puder</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-xl text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="font-semibold text-slate-800">Voluntariado</h3>
              <p className="text-sm text-slate-500 mt-1">Doe o seu tempo e competências</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
